import React, { useState, useRef, useEffect } from 'react';
import * as yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import { Loader } from 'google-maps';
import { config } from "../core";
import { apiInspections } from "../api/inspections";

// Demo data configuration - set to true to show demo button
const SHOW_DEMO_BUTTON = false;

const DEMO_DATA = {
  policyNumber: 'CRC-12345-TEST',
  policyEffectiveDate: '2025-01-01',
  insuredName: 'John and Jane Smith',
  insuredPhoneNumber: '(555) 123-4567',
  insuredEmail: 'john.smith@email.com',
  agencyName: 'Premium Insurance Agency',
  producerName: 'Fred Jones',
  producerPrimaryPhone: '(555) 987-6543',
  propertyStreet: '123 Main Street',
  propertyCity: 'Miami',
  propertyState: 'FL',
  propertyZipCode: '33101',
  propertyCounty: 'Miami-Dade',
  inspectionType: 'CRC',
  // Excel fields
  policyCompany: 'Citizens Property Insurance',
  coverageA: '750000',
  construction: 'Frame',
  policyType: 'HO-3',
  yearBuilt: '2018',
  dwellingType: 'Single Family',
  numberOfFloors: '2',
  occupancy: 'Owner Occupied',
  electricalPanel: 'Circuit Breakers',
  squareFootage: '2500',
  roofYear: '2018',
  roofMaterials: 'Architectural Shingle',
  roofGeometry: 'Gable',
  roofCondition: 'Good',
  roofConnectedToWalls: 'Clips',
  windMitigation: 'Yes',
  pcClass: '3',
  burglarAlarm: 'No',
  fireAlarm: 'Yes',
  heatingACYear: '2018',
  plumbingYear: '2018',
  wiringYear: '2018',
};

// Tailwind input field component
const InputField = React.forwardRef(({ label, type = "text", value, onChange, error, helperText, required, placeholder, disabled, ...props }, ref) => {
  return (
    <div className="tw-w-full">
      <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
        {label} {required && <span className="tw-text-red-500">*</span>}
      </label>
      <input
        ref={ref}
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        className={`tw-block tw-w-full tw-rounded-md tw-border ${
          error ? 'tw-border-red-300' : 'tw-border-gray-300'
        } tw-px-3 tw-py-2 tw-shadow-sm focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-bg-gray-100`}
        {...props}
      />
      {(error || helperText) && 
        <p className={`tw-mt-1 tw-text-sm ${error ? 'tw-text-red-600' : 'tw-text-gray-500'}`}>
          {error || helperText}
        </p>
      }
    </div>
  );
});

InputField.displayName = 'InputField';

// Tailwind select component
const SelectField = ({ label, value, onChange, error, helperText, required, children, ...props }) => {
  return (
    <div className="tw-w-full">
      <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
        {label} {required && <span className="tw-text-red-500">*</span>}
      </label>
      <select
        value={value}
        onChange={onChange}
        className={`tw-block tw-w-full tw-rounded-md tw-border ${
          error ? 'tw-border-red-300' : 'tw-border-gray-300'
        } tw-px-3 tw-py-2 tw-shadow-sm focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500`}
        {...props}
      >
        {children}
      </select>
      {(error || helperText) && 
        <p className={`tw-mt-1 tw-text-sm ${error ? 'tw-text-red-600' : 'tw-text-gray-500'}`}>
          {error || helperText}
        </p>
      }
    </div>
  );
};

// Tailwind textarea component
const TextAreaField = ({ label, value, onChange, error, helperText, required, placeholder, rows = 4, ...props }) => {
  return (
    <div className="tw-w-full">
      <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
        {label} {required && <span className="tw-text-red-500">*</span>}
      </label>
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        className={`tw-block tw-w-full tw-rounded-md tw-border ${
          error ? 'tw-border-red-300' : 'tw-border-gray-300'
        } tw-px-3 tw-py-2 tw-shadow-sm focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500`}
        {...props}
      />
      {(error || helperText) && 
        <p className={`tw-mt-1 tw-text-sm ${error ? 'tw-text-red-600' : 'tw-text-gray-500'}`}>
          {error || helperText}
        </p>
      }
    </div>
  );
};

const validationSchema = yup.object({
  policyNumber: yup.string()
    .required('Policy # is required')
    .matches(
      /^[A-Za-z0-9-]+$/,
      'Policy # can only contain letters, numbers, and dashes (-)'
    ),
  policyEffectiveDate: yup.date()
    .transform((value, originalValue) => {
      // Handle empty strings by returning null instead of Invalid Date
      return originalValue === '' ? null : value;
    })
    .nullable()
    .required('Policy Effective Date is required'),
    
  insuredName: yup.string().required('Insured Name is required'),
  insuredPhoneNumber: yup.string().required('Insured Phone Number is required'),
    
  agencyName: yup.string().required('Agency Name is required'),
  producerPrimaryPhone: yup.string().required('Producer Primary Phone is required'),
    
  propertyStreet: yup.string().required('Property Street is required'),
  propertyCity: yup.string().required('Property City is required'),
  propertyState: yup.string().required('Property State is required'),
  propertyZipCode: yup.string().required('Property Zip Code is required'),
  propertyCounty: yup.string().required('Property County is required'),
    
  inspectionType: yup.string().oneOf(['CRC Basic', 'CRC All Inclusive']).required('Inspection Type is required'),
});

// Google Maps configuration
const googleMapsOptions = {
  version: "weekly",
  libraries: ["places", "geometry"]
};

export default function CRCManualOrder() {
  const addressInputRef = useRef();
  
  // Initialize Google Maps loader inside component where config is available
  const [loader, setLoader] = useState(null);
  const [autocompleteLoaded, setAutocompleteLoaded] = useState(false);
  
  // Initialize Google Maps loader
  useEffect(() => {
    if (config.mapsKeyPlanner) {
      const googleLoader = new Loader(config.mapsKeyPlanner, googleMapsOptions);
      setLoader(googleLoader);
    } else {
      console.warn('Google Maps key not available, using manual address entry');
    }
  }, []);
  
  const [formData, setFormData] = useState({
    policyNumber: '',
    policyEffectiveDate: '',
    insuredName: '',
    insuredPhoneNumber: '',
    insuredEmail: '',
    agencyName: '',
    producerName: '',
    producerPrimaryPhone: '',
    propertyStreet: '',
    propertyCity: '',
    propertyState: '',
    propertyZipCode: '',
    propertyCounty: '',
    inspectionType: '',
    // Excel fields - all optional
    policyCompany: '',
    coverageA: '',
    construction: '',
    policyType: '',
    yearBuilt: '',
    dwellingType: '',
    numberOfFloors: '',
    occupancy: '',
    electricalPanel: '',
    squareFootage: '',
    roofYear: '',
    roofMaterials: '',
    roofGeometry: '',
    roofCondition: '',
    roofConnectedToWalls: '',
    windMitigation: '',
    pcClass: '',
    burglarAlarm: '',
    fireAlarm: '',
    heatingACYear: '',
    plumbingYear: '',
    wiringYear: ''
  });
    
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Demo data function
  const fillDemoData = () => {
    setFormData(DEMO_DATA);
    setErrors({});
    toast.info('Demo data loaded!');
  };

  // Initialize Google Maps autocomplete
  useEffect(() => {
    if (addressInputRef.current && !autocompleteLoaded && loader) {
      console.log('Initializing Google Maps Autocomplete...');
      loader.load().then((google) => {
        console.log('Google Maps loaded successfully');
        const autocomplete = new google.maps.places.Autocomplete(addressInputRef.current, {
          types: ['address'],
          componentRestrictions: { country: 'us' } // Restrict to US addresses
        });

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (!place.geometry) {
            return;
          }

          // Parse the address components
          const addressComponents = place.address_components;
          let street = '';
          let city = '';
          let state = '';
          let zipcode = '';
          let county = '';

          addressComponents.forEach(component => {
            const types = component.types;
            
            if (types.includes('street_number')) {
              street = component.long_name + ' ';
            } else if (types.includes('route')) {
              street += component.long_name;
            } else if (types.includes('locality')) {
              city = component.long_name;
            } else if (types.includes('administrative_area_level_1')) {
              state = component.short_name;
            } else if (types.includes('postal_code')) {
              zipcode = component.long_name;
            } else if (types.includes('administrative_area_level_2')) {
              county = component.long_name.replace(' County', ''); // Remove "County" suffix
            }
          });

          // Update form data with parsed address
          setFormData(prev => ({
            ...prev,
            propertyStreet: street.trim(),
            propertyCity: city,
            propertyState: state,
            propertyZipCode: zipcode,
            propertyCounty: county
          }));
        });

        setAutocompleteLoaded(true);
      }).catch(err => {
        console.error('Failed to load Google Maps:', err);
      });
    }
  }, [loader, autocompleteLoaded, addressInputRef]);

  const checkPolicyNumberExists = async (policyNumber) => {
    if (!policyNumber || policyNumber.trim() === '') {return;}
    
    try {
      const response = await apiInspections.checkPolicyExists(policyNumber);
      if (response.data?.result?.exists) {
        setErrors(prev => ({
          ...prev,
          policyNumber: 'This policy number already exists in the system'
        }));
        return true;
      } else {
        // Clear policy number error if it was a duplicate error
        if (errors.policyNumber === 'This policy number already exists in the system') {
          setErrors(prev => ({
            ...prev,
            policyNumber: undefined
          }));
        }
        return false;
      }
    } catch (error) {
      console.error('Error checking policy existence:', error);
      return false;
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
        
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handlePolicyNumberBlur = async (event) => {
    const policyNumber = event.target.value;
    await checkPolicyNumberExists(policyNumber);
  };

  const validateForm = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      
      // Check for policy number duplicates
      const policyExists = await checkPolicyNumberExists(formData.policyNumber);
      if (policyExists) {
        return false;
      }
      
      setErrors({});
      return true;
    } catch (validationErrors) {
      const errorObj = {};
      validationErrors.inner.forEach(error => {
        errorObj[error.path] = error.message;
      });
      setErrors(errorObj);
      
      // Scroll to first error field
      if (validationErrors.inner.length > 0) {
        const firstErrorField = validationErrors.inner[0].path;
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
          element.focus();
        }
      }
      
      return false;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
        
    const isValid = await validateForm();
    if (!isValid) {
      toast.error('Please fix the validation errors before submitting');
      return;
    }

    setLoading(true);
        
    try {
      const response = await apiInspections.createCRCManualInspection(formData);
      
      // Check for successful response - status is in response.data.status, not response.status
      if (response.data.status === 200 && response.data?.result?.success === true) {
        const data = response.data.result;
        toast.success(`Successfully created inspection - Policy: ${data.policyNumber}`);
                
        // Reset form
        setFormData({
          policyNumber: '',
          policyEffectiveDate: '',
          insuredName: '',
          insuredPhoneNumber: '',
          insuredEmail: '',
          agencyName: '',
          producerName: '',
          producerPrimaryPhone: '',
          propertyStreet: '',
          propertyCity: '',
          propertyState: '',
          propertyZipCode: '',
          propertyCounty: '',
          inspectionType: '',
          // Excel fields - all optional
          policyCompany: '',
          coverageA: '',
          construction: '',
          policyType: '',
          yearBuilt: '',
          dwellingType: '',
          numberOfFloors: '',
          occupancy: '',
          electricalPanel: '',
          squareFootage: '',
          roofYear: '',
          roofMaterials: '',
          roofGeometry: '',
          roofCondition: '',
          roofConnectedToWalls: '',
          windMitigation: '',
          pcClass: '',
          burglarAlarm: '',
          fireAlarm: '',
          heatingACYear: '',
          plumbingYear: '',
          wiringYear: ''
        });
        
        // Scroll to top on success
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        console.error('Form submission error:', response.data);
        
        // Handle API validation errors
        if (response.data && response.data.errors && response.data.errors.length > 0) {
          const apiErrors = {};
          response.data.errors.forEach(error => {
            apiErrors[error.key] = error.message;
          });
          setErrors(apiErrors);
          toast.error('Please fix the validation errors and try again.');
        } else {
          toast.error('Failed to submit form. Please check your data and try again.');
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tw-p-6 tw-max-w-6xl tw-mx-auto">
      <h1 className="tw-text-3xl tw-font-bold tw-mb-6">
        CRC Manual Inspection Order
      </h1>

      {SHOW_DEMO_BUTTON && 
        <div className="tw-mb-6">
          <button
            type="button"
            onClick={fillDemoData}
            className="tw-bg-blue-600 tw-text-white tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2"
          >
            🚀 Fill Demo Data
          </button>
          <p className="tw-text-sm tw-text-gray-500 tw-mt-1">
            Click to populate the form with test data for easy testing
          </p>
        </div>
      }
            
      <form onSubmit={handleSubmit}>
        {/* Order Information Section */}
        <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-6 tw-mb-6">
          <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-4">
            Order Information
          </h2>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
            <InputField
              label="Policy #"
              name="policyNumber"
              value={formData.policyNumber}
              onChange={handleChange('policyNumber')}
              onBlur={handlePolicyNumberBlur}
              error={errors.policyNumber}
              required
            />
            <InputField
              label="Policy Effective Date"
              type="date"
              name="policyEffectiveDate"
              value={formData.policyEffectiveDate}
              onChange={handleChange('policyEffectiveDate')}
              error={errors.policyEffectiveDate}
              required
            />
          </div>
        </div>

        {/* Insured Information Section */}
        <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-6 tw-mb-6">
          <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-4">
            Insured Information
          </h2>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <InputField
              label="Insured Name"
              name="insuredName"
              value={formData.insuredName}
              onChange={handleChange('insuredName')}
              error={errors.insuredName}
              required
            />
            <InputField
              label="Insured Phone Number"
              name="insuredPhoneNumber"
              value={formData.insuredPhoneNumber}
              onChange={handleChange('insuredPhoneNumber')}
              error={errors.insuredPhoneNumber}
              required
            />
            <InputField
              label="Insured Email"
              name="insuredEmail"
              value={formData.insuredEmail}
              onChange={handleChange('insuredEmail')}
              error={errors.insuredEmail}
            />
          </div>
        </div>

        {/* Agency Information Section */}
        <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-6 tw-mb-6">
          <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-4">
            Agency Information
          </h2>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <InputField
              label="Agency Name"
              name="agencyName"
              value={formData.agencyName}
              onChange={handleChange('agencyName')}
              error={errors.agencyName}
              required
            />
            <InputField
              label="Producer Name"
              name="producerName"
              value={formData.producerName}
              onChange={handleChange('producerName')}
              error={errors.producerName}
            />
            <InputField
              label="Producer Primary Phone"
              name="producerPrimaryPhone"
              value={formData.producerPrimaryPhone}
              onChange={handleChange('producerPrimaryPhone')}
              error={errors.producerPrimaryPhone}
              required
            />
          </div>
        </div>

        {/* Property Information Section */}
        <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-6 tw-mb-6">
          <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-4">
            Property Information
          </h2>
          <div className="tw-space-y-4">
            {loader && 
              <InputField
                label="Property Address (Start typing for suggestions)"
                placeholder="Enter property address..."
                ref={addressInputRef}
                value={formData.propertyStreet}
                onChange={handleChange('propertyStreet')}
                autoComplete="off"
                helperText="Start typing an address for suggestions or fill in the fields below manually"
              />
            }
            
            <div className="tw-grid tw-grid-cols-1 tw-gap-4">
              <InputField
                label="Property Street"
                value={formData.propertyStreet}
                onChange={handleChange('propertyStreet')}
                error={errors.propertyStreet}
                required
              />
            </div>
            
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
              <InputField
                label="Property City"
                name="propertyCity"
                value={formData.propertyCity}
                onChange={handleChange('propertyCity')}
                error={errors.propertyCity}
                required
              />
              <InputField
                label="Property County"
                name="propertyCounty"
                value={formData.propertyCounty}
                onChange={handleChange('propertyCounty')}
                error={errors.propertyCounty}
                required
              />
           
              <InputField
                label="Property State"
                name="propertyState"
                value={formData.propertyState}
                onChange={handleChange('propertyState')}
                error={errors.propertyState}
                required
              />
              <InputField
                label="Property Zip Code"
                name="propertyZipCode"
                value={formData.propertyZipCode}
                onChange={handleChange('propertyZipCode')}
                error={errors.propertyZipCode}
                required
              />
            </div>
          </div>
        </div>

        {/* Inspection Details Section */}
        <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-6 tw-mb-6">
          <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-4">
            Inspection Details
          </h2>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <SelectField
              label="Inspection Type"
              name="inspectionType"
              value={formData.inspectionType}
              onChange={handleChange('inspectionType')}
              error={errors.inspectionType}
              required
            >
              <option value="">Select an inspection type</option>
              <option value="CRC Basic">C+ (Basic)</option>
              <option value="CRC All Inclusive">CRC (All Inclusive)</option>
            </SelectField>
          </div>
        </div>

        {/* Policy Information Section */}
        <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-6 tw-mb-6">
          <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-4">
            Policy Information
          </h2>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
            <InputField
              label="Policy Company"
              value={formData.policyCompany}
              onChange={handleChange('policyCompany')}
            />
            <InputField
              label="Coverage A"
              value={formData.coverageA}
              onChange={handleChange('coverageA')}
              placeholder="eg 250000"
            />
            <InputField
              label="Policy Type"
              value={formData.policyType}
              onChange={handleChange('policyType')}
              placeholder="e.g., HO-3, HO-6"
            />
            <InputField
              label="Policy Expiration Date"
              type="date"
              value={formData.policyExpirationDate}
              onChange={handleChange('policyExpirationDate')}
            />
          </div>
        </div>

        {/* Property Construction Section */}
        <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-6 tw-mb-6">
          <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-4">
            Property Construction Details
          </h2>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
            <InputField
              label="Construction"
              value={formData.construction}
              onChange={handleChange('construction')}
              placeholder="e.g., Frame, Masonry, Steel"
            />
            <InputField
              label="Year Built"
              type="number"
              value={formData.yearBuilt}
              onChange={handleChange('yearBuilt')}
              placeholder="e.g., 1985"
            />
            <InputField
              label="Dwelling Type"
              value={formData.dwellingType}
              onChange={handleChange('dwellingType')}
              placeholder="e.g., Single Family, Condo"
            />
            <InputField
              label="Number of Floors"
              type="number"
              value={formData.numberOfFloors}
              onChange={handleChange('numberOfFloors')}
            />
            <InputField
              label="Occupancy"
              value={formData.occupancy}
              onChange={handleChange('occupancy')}
              placeholder="e.g., Owner Occupied, Tenant"
            />
            <InputField
              label="Square Footage"
              type="number"
              value={formData.squareFootage}
              onChange={handleChange('squareFootage')}
            />
          </div>
        </div>

        {/* Roof Information Section */}
        <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-6 tw-mb-6">
          <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-4">
            Roof Information
          </h2>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
            <InputField
              label="Roof Year"
              type="number"
              value={formData.roofYear}
              onChange={handleChange('roofYear')}
            />
            <InputField
              label="Roof Materials"
              value={formData.roofMaterials}
              onChange={handleChange('roofMaterials')}
              placeholder="e.g., Shingle, Tile, Metal"
            />
            <InputField
              label="Roof Geometry"
              value={formData.roofGeometry}
              onChange={handleChange('roofGeometry')}
              placeholder="e.g., Gable, Hip, Flat"
            />
            <InputField
              label="Roof Condition"
              value={formData.roofCondition}
              onChange={handleChange('roofCondition')}
              placeholder="e.g., Good, Fair, Poor"
            />
            <InputField
              label="Roof Connected to Walls"
              value={formData.roofConnectedToWalls}
              onChange={handleChange('roofConnectedToWalls')}
              placeholder="e.g., Clips, Straps, Nails"
            />
            <InputField
              label="Wind Mitigation"
              value={formData.windMitigation}
              onChange={handleChange('windMitigation')}
              placeholder="e.g., Yes, No, Partial"
            />
          </div>
        </div>

        {/* Systems Information Section */}
        <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-6 tw-mb-6">
          <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-4">
            Systems Information
          </h2>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
            <InputField
              label="Electrical Panel"
              value={formData.electricalPanel}
              onChange={handleChange('electricalPanel')}
              placeholder=""
            />
            <InputField
              label="Heating / AC Year"
              type="number"
              value={formData.heatingACYear}
              onChange={handleChange('heatingACYear')}
            />
            <InputField
              label="Plumbing Year"
              type="number"
              value={formData.plumbingYear}
              onChange={handleChange('plumbingYear')}
            />
            <InputField
              label="Wiring Year"
              type="number"
              value={formData.wiringYear}
              onChange={handleChange('wiringYear')}
            />
          </div>
        </div>

        {/* Security and Protection Section */}
        <div className="tw-bg-white tw-shadow tw-rounded-lg tw-p-6 tw-mb-6">
          <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-4">
            Security and Protection
          </h2>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
            <InputField
              label="PC Class"
              value={formData.pcClass}
              onChange={handleChange('pcClass')}
              placeholder="e.g., 1, 2, 3, etc."
            />
            <SelectField
              label="Burglar Alarm"
              value={formData.burglarAlarm}
              onChange={handleChange('burglarAlarm')}
            >
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </SelectField>
            <SelectField
              label="Fire Alarm"
              value={formData.fireAlarm}
              onChange={handleChange('fireAlarm')}
            >
              <option value="">Select</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </SelectField>
          </div>
        </div>

       
        {/* Submit Button */}
        <div className="tw-flex tw-justify-center">
          <button
            type="submit"
            disabled={loading}
            className="tw-bg-blue-600 tw-text-white tw-px-6 tw-py-3 tw-rounded-lg tw-font-medium tw-shadow-sm hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-blue-500 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-min-w-[200px] tw-flex tw-items-center tw-justify-center"
          >
            {loading ? 
              <>
                <svg className="tw-animate-spin tw--ml-1 tw-mr-3 tw-h-5 tw-w-5 tw-text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Inspection...
              </>
              : 
              'Create Inspection'
            }
          </button>
        </div>
      </form>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
      />
    </div>
  );
}